import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SingleSelectComponent } from '@examdojo/core/select';
import { THEME_COLORS_WITH_SHADES } from '../../elements/theme';
import type { CanvasConfig } from '../../jsxgraph.model';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'dojo-canvas-editor',
  standalone: true,
  imports: [ReactiveFormsModule, MatSlideToggleModule, SingleSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-2 p-4">
      <div class="grid grid-cols-2 gap-1.5">
        <dojo-single-select
          label="Background"
          [formCtrl]="bgColorCtrl"
          [options]="bgColorOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          placeholder="Default"
          (selectionChange)="onBgColorChange($event)"
        />
        <ng-content />
      </div>
      <mat-slide-toggle [checked]="borderVisible()" (change)="onBorderVisibleChange($event.checked)"
        >Border</mat-slide-toggle
      >
      @if (borderVisible()) {
        <dojo-single-select
          label="Border Color"
          [formCtrl]="borderColorCtrl"
          [options]="borderColorOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          (selectionChange)="onBorderColorChange($event)"
        />
      }
    </div>
  `,
})
export class CanvasEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.canvasConfig();
      this.syncFromConfig();
    });
  }

  readonly canvasConfig = input<CanvasConfig>();
  readonly canvasConfigChange = output<CanvasConfig>();
  /** Default border visibility when canvas config doesn't specify it. */
  readonly defaultBorderVisible = input(true);

  readonly bgColorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly borderColorCtrl = new FormControl<string>('', { nonNullable: true });

  readonly bgColorOptions: SelectOption[] = THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c }));
  readonly borderColorOptions: SelectOption[] = [
    { label: 'Default (neutral-200)', value: 'neutral-200' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  readonly borderVisible = computed(() => this.canvasConfig()?.border?.visible ?? this.defaultBorderVisible());

  onBgColorChange(color: unknown): void {
    const raw = (color as string) || undefined;
    const resolved = raw && !raw.includes('-') ? `${raw}-50` : raw;
    const current = this.canvasConfig() ?? {};
    this.canvasConfigChange.emit({ ...current, color: resolved as typeof current.color });
  }

  onBorderVisibleChange(visible: boolean): void {
    const current = this.canvasConfig() ?? {};
    const currentBorder = current.border ?? {};
    this.canvasConfigChange.emit({ ...current, border: { ...currentBorder, visible } });
  }

  onBorderColorChange(color: unknown): void {
    const raw = (color as string) || undefined;
    const resolved = raw && !raw.includes('-') ? `${raw}-200` : raw;
    const current = this.canvasConfig() ?? {};
    const currentBorder = current.border ?? {};
    this.canvasConfigChange.emit({
      ...current,
      border: { ...currentBorder, color: resolved as typeof currentBorder.color },
    });
  }

  private syncFromConfig(): void {
    const canvas = this.canvasConfig();
    this.bgColorCtrl.setValue(canvas?.color ?? '', { emitEvent: false });
    this.borderColorCtrl.setValue(canvas?.border?.color ?? '', { emitEvent: false });
  }
}
